import { AfterViewInit, Component } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { ColumnApi, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { EditDeleteActionsRenderer } from "src/app/components/shared/ag-grid/cell-renderers/edit-delete.component";
import { Utilities } from "src/app/models/utilities";
import { PasswordValidation } from "src/app/models/validator";
import { AppCoreService } from "src/app/services/app-core.service";
import { CustomerService } from "src/app/services/customer.service";
import { VendorService } from "src/app/services/vendor.service";
import Swal from "sweetalert2";

@Component({
  selector: 'account-management',
  templateUrl: './account-management.component.html',
})
export class AccountManagementComponent implements AfterViewInit {
  accManageForm: FormGroup;
  objectKeys = Object.keys;
  form = new FormGroup({});
  showDynamicFields = false;

  vendorId: number;
  id: number;
  permConfig = {
    isViewAllowed: false,
    isAddAllowed: false,
    isEditAllowed: false,
    isDeleteAllowed: false
  };
  isFormSubmitted = false;
  isShowAdditionalDetails = false;
  isShowConfig = false;
  vendorName: string;
  editMode = false;
  vendorDetails: any;
  accId: number;
  private gridApi!: GridApi;
  private gridColumnApi!: ColumnApi;
  public context: any;
  defaultColDef = {
    sortable: true,
    resizable: false,
  };
  gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 10,
    domLayout: 'autoHeight',
  }
  customerList: [];
  columnDefs = [
    { headerName: 'Account Code', field: 'accountcode', filter: true },
    { headerName: 'User Id', field: 'userid', filter: true },
    {
      headerName: 'Actions',
      field: 'accountid',
      cellRenderer: EditDeleteActionsRenderer,
      cellClass: "actions-button-cell",
      cellStyle: { "overflow": 'visible' },
      colId: 'params',
      editable: false,
      sortable: false,
      minWidth: 150,
    },
  ];
  mandateCustomer = false;
  rowData = [];
  useEnterKey: any;
  jsonData: { [key: string]: any }; 
  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private appCoreService: AppCoreService,
    private vendorService: VendorService,
    private customerService: CustomerService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private titleService: Title,
    private utilities: Utilities,
  ) {
    titleService.setTitle('Vendors');
    this.context = { componentParent: this };
  }

  ngOnInit(): void {
    this.vendorId = this.route.snapshot.params['id'];
    this.accManageForm = this.formBuilder.group({
      txtVendor: new FormControl({ value: null, disabled: true }),
      txtAccCode: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
      txtUserId: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
      txtPassword: [null, Validators.compose([Validators.required, PasswordValidation])],
      ddlCustomers: [null]
    });

    this.isFormSubmitted = false;
    this.checkPermission();
    if (this.permConfig.isEditAllowed) {
      this.bindVendorDetailsById();
      this.getAllAccounts(this.vendorId);
      this.getAllCustomers();
    }
  }

  ngAfterViewInit(): void {
    this.useEnterKey ? this.utilities.focusFirstElement(this.accManageForm) : null;
  }

  checkPermission(): any {
    const userData = this.appCoreService.GetUserData();
    this.useEnterKey = !userData.metaInfo.tabbasedtemplate;

    const permission = userData.config.filter(x => x.categoryname === 'Vendors');
    if (permission.length) {
      this.permConfig.isViewAllowed = permission[0].view;
      this.permConfig.isAddAllowed = permission[0].add;
      this.permConfig.isEditAllowed = permission[0].edit;
      this.permConfig.isDeleteAllowed = permission[0].delete;
    }
  }

  getAllCustomers() {
    try {
      this.customerService.GetAll({ scid: this.appCoreService.GetUserData().selectedServiceCenter, isactive: this.editMode ? false : true }).subscribe((res) => {
        if (res && res.success) {
          this.customerList = res.data;
        }
      })
    } catch (error) {

    }
  }
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    params.api.sizeColumnsToFit();
  }
  bindVendorDetailsById() {
    this.vendorService.GetById(this.vendorId).subscribe((res) => {
      this.ngxUiLoaderService.start();
      try {
        if (res && res.success) {
          this.ngxUiLoaderService.stop();
          this.vendorDetails = res.data[0];
          this.accManageForm.controls.txtVendor.setValue(res.data[0]?.name);
          this.checkVendorMandate();
          this.ngxUiLoaderService.stop();
        }
      } catch (error) {
        this.ngxUiLoaderService.stop();
      }
    })
  }

  //need to be reviwed as hardcoded for 'XTN'
  checkVendorMandate() {
    if (this.accManageForm.get('txtVendor').value == 'XTN') {
      this.mandateCustomer = true;
      this.accManageForm.get('ddlCustomers').setValidators([Validators.required]);
      this.accManageForm.get('ddlCustomers').updateValueAndValidity();
    }
  }

  upsert() {
    if (!this.permConfig.isAddAllowed && !this.editMode) {
      this.warningDialog('No access to Add.');
      return;
    }
    else if (!this.permConfig.isEditAllowed && this.editMode) {
      this.warningDialog('No access to Edit.');
      return;
    }
    this.isFormSubmitted = true;
    if (this.editMode) {
      this.accManageForm.controls.txtPassword.clearValidators();
      this.accManageForm.controls.txtPassword.updateValueAndValidity();
    }
    if (this.accManageForm.valid && this.permConfig.isAddAllowed || this.accManageForm.valid && this.permConfig.isEditAllowed) {
      const customer: any = this.customerList.filter((x: any) => x.id === this.accManageForm.controls.ddlCustomers.value);
      const additionaldetails = {
        customercode: customer[0]?.code
      }
      const lowercaseAccountCode = this.accManageForm.controls.txtAccCode.value.trim();
      const formValues = this.form.value;
      const reqObj = {
        id: this.accId || null,
        vendorid: this.vendorId,
        vendorcode: this.vendorDetails.code,
        accountcode: lowercaseAccountCode,
        userid: this.accManageForm.controls.txtUserId.value?.trim(),
        password: this.accManageForm.controls.txtPassword.value?.trim(),
        additionaldetails: formValues || null
      }

      if (this.editMode) {
        this.update(reqObj);
      }
      else
        this.add(reqObj)
    }
  }

  add(reqObj: any) {
    this.ngxUiLoaderService.start();
    this.vendorService.AddAccount(reqObj).subscribe((res) => {
      try {
        if (res && !res.success) {
          this.ngxUiLoaderService.stop();
          this.errorDialog(res.message);
        }
        if (res && res.success) {
          this.ngxUiLoaderService.stop();
          this.openSuccessDialog("Vendor account added successfully").then((result) => {
            if (result.isConfirmed) {
              this.getAllAccounts(this.vendorId);
              this.resetForm();
            }
          });
        }
      }
      catch (err) {
        console.error(err);
      }
    });
  }

  update(reqObj: any) {
    this.ngxUiLoaderService.start();
    this.vendorService.UpdateAccount(reqObj).subscribe((res) => {
      try {
        if (res && res?.success) {
          this.ngxUiLoaderService.stop();
          this.openSuccessDialog("Record updated successfully").then((res) => {
            if (res.isConfirmed) {
              const formValues = this.form.value;
              this.getAllAccounts(this.vendorId);
              this.resetForm();
            }
          })
        } else if (res && !res.success) {
          this.ngxUiLoaderService.stop();
        }
      }
      catch (err) {
        console.error(err);
      }
    });
  }

  editAction(cell: any) {
    try {
      if (!this.permConfig.isEditAllowed) {
        this.warningDialog('No access to Edit.');
      }
      else if (this.permConfig.isEditAllowed && cell) {
        this.vendorService.GetAllAccountsByVendorId(cell.id).subscribe((res) => {
          if (res && res.success) {

            if(res.data.length > 0){
              let selectedVendorDetails = res.data.filter(x => x.accountcode === cell.accountcode);
              this.jsonData = selectedVendorDetails[0].additionaldetails;
              this.accId = selectedVendorDetails[0].id;
            }else{
              this.jsonData = res.data[0].additionaldetails;
              this.accId = cell.id;
            }
            
            this.accManageForm.patchValue({ 
              txtAccCode: cell.accountcode,
              txtUserId: cell.userid
            });
            this.editMode = true;
            this.showDynamicFields = false;
            this.accManageForm.controls.txtPassword.clearValidators();
            this.accManageForm.controls.txtAccCode.disable();
            this.accManageForm.controls.txtAccCode.clearValidators();
            this.accManageForm.controls.txtPassword.updateValueAndValidity();
            this.accManageForm.controls.txtAccCode.updateValueAndValidity();
            //populate additional details for row edit
            const customer: any = this.customerList.find((x: any) => x.code == cell.additionaldetails.customercode);
            this.accManageForm.get('ddlCustomers').setValue(customer?.id);
            this.getAllCustomers();
            this.createDynamicForm(this.jsonData);
          }
        })
      }
    } catch (e) {
      console.error(e);
    }
  }

  deleteAction(cell: any) {
    if (!this.permConfig.isDeleteAllowed) {
      this.warningDialog('No access to Delete.');
    }
    else if (this.permConfig.isDeleteAllowed && cell && cell.accountid) {
      this.openConfirmationDialog('Are you sure you want to delete ?', cell.accountid);
    }
  }

  deleteRecord(id) {
    try {
      if (id) {
        this.vendorService.DeleteAccount(this.vendorId, id).subscribe((res) => {
          if (res && res.success) {
            this.getAllAccounts(this.vendorId);
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  async openConfirmationDialog(msg: any, id?: number) {
    const result = await Swal.fire({
      titleText: 'Confirmation',
      text: msg,
      icon: 'warning',
      confirmButtonText: 'Yes',
      showDenyButton: true,
      denyButtonText: 'No',
      allowEscapeKey: false,
      allowOutsideClick: false
    });
    if (result.isConfirmed) {
      this.deleteRecord(id);
      this.getAllAccounts(this.vendorId);
    }
  }

  errorDialog(msg: any) {
    Swal.fire({
      title: 'Opps!',
      text: msg,
      icon: 'error',
      allowEscapeKey: false,
      allowOutsideClick: false
    })
  }

  async openSuccessDialog(msg: any) {
    const result = await Swal.fire({
      title: 'Success!',
      text: msg,
      icon: 'success',
      confirmButtonText: 'OK',
      allowEscapeKey: false,
      allowOutsideClick: false
    });

    return result;
  }

  warningDialog(msg: any) {
    Swal.fire({
      title: 'Warning!',
      text: msg,
      icon: 'warning',
      confirmButtonText: 'OK',
      allowEscapeKey: false,
      allowOutsideClick: false
    });
  }

  getAllAccounts(vendorId: number) {
    this.ngxUiLoaderService.start();
    this.vendorService.GetAllAccounts(vendorId).subscribe((res) => {
      try {
        if (res && res.success) {
          this.ngxUiLoaderService.stop();
          this.rowData = res.data;
          this.jsonData =this.rowData[0].additionaldetails;
          if(this.jsonData && Object.keys(this.jsonData).length > 0){
            this.isShowAdditionalDetails = true;
          }
          this.showDynamicFields = true;
          this.createDynamicForm(this.jsonData);
          this.setEmptyValues();
        }
      } catch (error) {
        this.ngxUiLoaderService.stop();
        console.error(error)
      }
    });
  }

  resetForm() {
    this.editMode = false;
    this.accManageForm.controls.txtAccCode.enable();
    this.accManageForm.controls.txtPassword.setValidators([Validators.required]);
    this.accManageForm.controls.txtPassword.updateValueAndValidity();
    this.accManageForm.controls.txtAccCode.setValidators([Validators.required]);
    this.accManageForm.controls.txtAccCode.updateValueAndValidity();
    const vendor = this.accManageForm.controls.txtVendor.value;
    this.accManageForm.reset();
    this.accManageForm.controls.txtVendor.setValue(vendor);
    this.isFormSubmitted = false;
    this.setEmptyValues();
  }

  createDynamicForm(data: any) {
    let group: any = {};

    Object.keys(data).forEach(key => {
      group[key] = new FormControl(data[key]);
    });

    this.form = new FormGroup(group);
  }

  setEmptyValues() {
    Object.keys(this.jsonData).forEach(key => {
      this.form.get(key)?.setValue('');
    });
  }

}
